/* ════════════ THE SNAPSHOTS ════════════
   The microsoft page's atmosphere: window frames pop open at random,
   hold a beat, take a little camera-flash screenshot of themselves,
   then minimize toward the corner and vanish. Recall, ambiently.
   Your screen, filed every few seconds. For your convenience. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function snapshots(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#snapshots")!;
  const ctx = c.getContext("2d")!;

  const TEAL = "#2ee6c8";
  const GREY = "#9aa0a6";

  type Win = {
    x: number;
    y: number;
    w: number;
    h: number;
    t: number; // age in frames
    life: number;
    col: string;
  };
  let wins: Win[] = [];

  const spawn = (): Win => {
    const isHot = Math.random() < 0.18;
    const w = 36 + Math.random() * 64;
    return {
      x: Math.random() * c.width,
      y: Math.random() * c.height,
      w,
      h: w * (0.55 + Math.random() * 0.25),
      t: 0,
      life: 240 + Math.random() * 280,
      col: isHot ? TEAL : GREY,
    };
  };

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    const count = Math.min(18, Math.floor((c.width * c.height) / 100000));
    wins = Array.from({ length: count }, () => {
      const w = spawn();
      w.t = Math.random() * w.life; // desync
      return w;
    });
  };
  seed();
  addEventListener("resize", seed, { signal });

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    // home corner: bottom-left, where minimized windows go to be forgotten
    const hx = 24;
    const hy = c.height - 24;
    for (const w of wins) {
      const p = w.t / w.life;
      const hot = w.col === TEAL;
      const base = hot ? 0.45 : 0.18;

      // phases: open 0–0.12 · hold 0.12–0.72 (flash ~0.66) · minimize 0.78–1
      let x = w.x;
      let y = w.y;
      let scale = 1;
      let a = base;

      if (p < 0.12) {
        // pop open: frame grows out of the title bar
        scale = p / 0.12;
        a = base * scale;
      } else if (p > 0.78) {
        // minimize: shrink and fly home
        const q = (p - 0.78) / 0.22;
        const e = q * q; // ease-in, it accelerates as it leaves you
        x = w.x + (hx - w.x) * e;
        y = w.y + (hy - w.y) * e;
        scale = 1 - 0.92 * e;
        a = base * (1 - e);
      }

      const ww = w.w * scale;
      const wh = w.h * scale;
      const bar = Math.max(2, 5 * scale);

      ctx.globalAlpha = a;
      ctx.strokeStyle = w.col;
      ctx.lineWidth = 1;
      ctx.strokeRect(x - ww / 2, y - wh / 2, ww, wh);
      // title bar with its tiny, useless close button
      ctx.beginPath();
      ctx.moveTo(x - ww / 2, y - wh / 2 + bar);
      ctx.lineTo(x + ww / 2, y - wh / 2 + bar);
      ctx.stroke();
      ctx.fillStyle = w.col;
      ctx.fillRect(x + ww / 2 - bar + 1, y - wh / 2 + 1, bar - 2, bar - 2);

      // the screenshot: a quick camera-flash fill just before minimizing
      if (p > 0.64 && p < 0.7) {
        const f = 1 - Math.abs((p - 0.67) / 0.03);
        ctx.globalAlpha = a * f * 0.5;
        ctx.fillRect(x - ww / 2, y - wh / 2 + bar, ww, wh - bar);
      }
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    for (let i = 0; i < wins.length; i++) {
      const w = wins[i]!;
      w.t++;
      if (w.t >= w.life) wins[i] = spawn();
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

onPage(snapshots);
